import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { api } from "@/lib/config";

/**
 * Pet Care AI chat widget — premium, accessible, brand-consistent.
 *
 * Key legibility choices (per the brand/readability audit):
 *  - All conversational text in a clean sans stack (Inter / system-ui)
 *    with 15px body and 1.55 line-height.
 *  - Near-black `#1a1a1a` body copy on white bot bubbles (AA contrast
 *    17:1 on white) — no more hard-to-read cream-on-cream serif.
 *  - User bubbles use deep forest with `#ffffff` text (AAA contrast).
 *  - Muted helper copy still uses `#5f584a` which remains above WCAG
 *    4.5:1 on cream backgrounds.
 *  - Messages animate in with a 160 ms subtle slide-up on mount.
 */

const genSessionId = () => {
  try {
    const existing = window.localStorage?.getItem("hopsy_chat_session");
    if (existing) return existing;
    const id = `hopsy-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    window.localStorage?.setItem("hopsy_chat_session", id);
    return id;
  } catch {
    return `hopsy-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
};

const SUGGESTIONS = [
  "Which hay is best for my 3-month-old bunny?",
  "How much alfalfa should I feed a nursing mother?",
  "Is my guinea pig getting enough vitamin C?",
  "Best bedding for a rabbit with sensitive paws?",
];

const SANS = '"Inter", "Helvetica Neue", system-ui, -apple-system, sans-serif';
const INK = "#1a1a1a";
const INK_MUTED = "#5f584a";

export default function PetCareAI() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(genSessionId);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hello! I'm Hopsy's Pet Care Assistant. Ask me anything about your rabbit, guinea pig, hamster or chinchilla's diet, health, or habitat.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages, open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: content }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat/pet-care", { session_id: sessionId, message: content });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "Sorry — I ran into an issue. Please try again in a moment." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes hopsy-chat-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hopsy-msg-in { animation: hopsy-chat-in 180ms ease-out both; }
      `}</style>

      {/* Floating button */}
      {!open && (
        <button
          data-testid="open-ai-chat"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-forest hover:bg-forest-hover text-cream shadow-[0_8px_30px_rgba(44,74,59,0.35)] rounded-full h-14 w-14 flex items-center justify-center transition-transform hover:scale-105"
          aria-label="Open Pet Care AI Assistant"
        >
          <MessageCircle className="w-6 h-6" strokeWidth={1.5} />
          <span className="absolute -top-1 -right-1 bg-sun text-forest h-5 w-5 rounded-full flex items-center justify-center">
            <Sparkles className="w-3 h-3" strokeWidth={2} />
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-6 right-6 z-50 w-[calc(100vw-1.5rem)] sm:w-[400px] h-[calc(100vh-6rem)] sm:h-[600px] max-w-[440px] bg-white border border-line rounded-2xl shadow-[0_20px_60px_rgba(44,74,59,0.25)] flex flex-col overflow-hidden animate-fade-up"
          style={{ fontFamily: SANS }}
        >
          {/* Header */}
          <div className="bg-forest px-5 py-4 text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-sun text-forest flex items-center justify-center shrink-0 shadow-[inset_0_-2px_4px_rgba(0,0,0,0.08)]">
                <Sparkles className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-base font-semibold leading-tight tracking-tight" style={{ fontFamily: SANS }}>
                  Pet Care AI
                </p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-white/75 mt-0.5 font-medium">
                  Powered by Hopsy
                </p>
              </div>
            </div>
            <button
              data-testid="close-ai-chat"
              onClick={() => setOpen(false)}
              className="h-8 w-8 flex items-center justify-center rounded-full text-white/80 hover:text-white hover:bg-white/10 transition"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-5 space-y-3"
            style={{ background: "#F8F5EC", scrollBehavior: "smooth" }}
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} hopsy-msg-in`}
                data-testid={`chat-msg-${m.role}`}
              >
                <div
                  className="max-w-[82%] px-4 py-3 whitespace-pre-wrap"
                  style={
                    m.role === "user"
                      ? {
                          background: "#2f3e2d",
                          color: "#ffffff",
                          borderRadius: "16px 16px 4px 16px",
                          fontFamily: SANS,
                          fontSize: "15px",
                          lineHeight: "1.55",
                          boxShadow: "0 2px 8px rgba(44,74,59,0.15)",
                        }
                      : {
                          background: "#ffffff",
                          color: INK,
                          borderRadius: "16px 16px 16px 4px",
                          border: "1px solid #ece4d0",
                          fontFamily: SANS,
                          fontSize: "15px",
                          lineHeight: "1.55",
                          boxShadow: "0 2px 8px rgba(44,74,59,0.06)",
                        }
                  }
                >
                  {m.text}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start hopsy-msg-in">
                <div
                  className="px-4 py-3 flex gap-1.5"
                  style={{
                    background: "#ffffff",
                    border: "1px solid #ece4d0",
                    borderRadius: "16px 16px 16px 4px",
                    boxShadow: "0 2px 8px rgba(44,74,59,0.06)",
                  }}
                  aria-label="Assistant is typing"
                >
                  <span className="h-2 w-2 bg-sage rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-2 w-2 bg-sage rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="h-2 w-2 bg-sage rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
                </div>
              </div>
            )}

            {messages.length === 1 && !loading && (
              <div className="pt-3 space-y-2">
                <p
                  className="uppercase tracking-[0.22em]"
                  style={{ color: "#2f3e2d", fontSize: "11px", fontWeight: 700, fontFamily: SANS }}
                >
                  Try asking
                </p>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid="chat-suggestion"
                    onClick={() => send(s)}
                    className="block w-full text-left px-3.5 py-3 transition bg-white hover:bg-cream-200 border border-line hover:border-forest rounded-lg"
                    style={{ fontFamily: SANS, fontSize: "14px", lineHeight: "1.45", color: INK, fontWeight: 500 }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-line bg-white p-3 flex items-center gap-2"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about your pet's diet, habitat, health…"
              aria-label="Type your question"
              className="flex-1 border border-line px-4 py-2.5 rounded-lg focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/15 transition"
              style={{
                fontFamily: SANS,
                fontSize: "15px",
                color: INK,
                background: "#F8F5EC",
              }}
            />
            <button
              data-testid="chat-send"
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-forest hover:bg-forest-hover text-white h-11 w-11 rounded-lg flex items-center justify-center transition disabled:opacity-40 disabled:cursor-not-allowed shrink-0 shadow-sm"
              aria-label="Send message"
            >
              <Send className="w-4 h-4" strokeWidth={2} />
            </button>
          </form>

          <p
            className="text-center pb-2 pt-0.5 bg-white"
            style={{ fontFamily: SANS, fontSize: "10px", color: INK_MUTED, letterSpacing: "0.02em" }}
          >
            Informational only · Always consult your vet for medical concerns
          </p>
        </div>
      )}
    </>
  );
}
